import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { HelpCircle, X, Cpu } from 'lucide-react';

interface HelpTooltipProps {
  title: string;
  content: string | React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  variant?: 'default' | 'ai-explainer';
  iconSize?: number;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({
  title,
  content,
  position = 'bottom',
  variant = 'default',
  iconSize = 16,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const popoverWidth = 320;
    let top = 0;
    let left = 0;

    switch (position) {
      case 'bottom':
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2 - popoverWidth / 2;
        break;
      case 'top':
        top = rect.top - 8;
        left = rect.left + rect.width / 2 - popoverWidth / 2;
        break;
      case 'right':
        top = rect.top;
        left = rect.right + 8;
        break;
      case 'left':
        top = rect.top;
        left = rect.left - popoverWidth - 8;
        break;
    }

    const maxLeft = window.innerWidth - popoverWidth - 16;
    left = Math.max(16, Math.min(left, maxLeft));

    setPopoverStyle({
      position: 'fixed',
      top: position === 'top' ? undefined : top,
      bottom: position === 'top' ? window.innerHeight - rect.top + 8 : undefined,
      left,
      width: popoverWidth,
      zIndex: 9999,
    });
  }, [position]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        popoverRef.current && !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  const isAI = variant === 'ai-explainer';

  const popover = isOpen ? ReactDOM.createPortal(
    <div
      ref={popoverRef}
      style={popoverStyle}
      className={`rounded-lg shadow-xl p-4 ${
        isAI
          ? 'bg-gradient-to-br from-blue-50 to-purple-50 border border-purple-200'
          : 'bg-white border border-gray-200'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center space-x-2">
          {isAI && <Cpu size={14} className="text-purple-600" />}
          <h4 className={`font-semibold ${isAI ? 'text-purple-800 text-sm' : 'text-super-dark-purple'}`}>
            {isAI ? 'Under the Hood' : title}
          </h4>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={14} />
        </button>
      </div>
      {isAI && (
        <p className="text-xs font-medium text-gray-800 mb-1.5">{title}</p>
      )}
      <div className={`leading-relaxed ${isAI ? 'text-xs text-gray-700' : 'text-sm text-gray-700'}`}>
        {content}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`transition-colors ${
          isAI
            ? 'text-purple-500 hover:text-purple-700'
            : 'text-electric-blue hover:text-dusky-purple'
        }`}
        aria-label="Show help information"
      >
        {isAI ? <Cpu size={iconSize} /> : <HelpCircle size={iconSize} />}
      </button>
      {popover}
    </div>
  );
};

export default HelpTooltip;
